import { cloneObject, deserify } from '@geneo2-web/shared-ui';
import {
  BloomLevelEnum,
  ImageType,
  Question,
  Question_QuestionEnum,
} from '@protos/content_management/content.db_pb';
import {
  QuestionModuleMap,
  QuestionSectionMeta,
  QuestionTypeMarks,
  SectionQuestionMetaData,
  TaskInfo,
} from '@protos/learning_management/lms.db_pb';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../reduxStore/reduxHooks';
import {
  setQPGAssessmentContent,
  setQPGContent,
  setQPGQuestionModuleMap,
  setQPGQuestionsSequenceInfo,
  setQPGQuestionTypeMarks,
} from '../../reducer/assessmentFlow.slice';
import { getQuestionTypeTitleAndEnum } from '../../Common/ReviewAssessment/functions';
import md5 from 'blueimp-md5';
import { interactionEvent } from '../../../Auth/auth.events';
import { useLocation } from 'react-router-dom';
import { useDownloadContext } from '../../../../app/Context/DownloadContextProviderV2';

export const useDeleteQPGQuestion = () => {
  const {
    qpg_assessment_content,
    qpg_questions_sequence_info,
    qpg_question_module_map,
  } = deserify(useAppSelector((state) => state.assessment));
  const dispatch = useAppDispatch();
  const qpgAssessmentQuestions =
    qpg_assessment_content?.map((ques) => new Question(ques)) || [];
  const qpgQuestionsSequenceInfo = new TaskInfo(
    cloneObject(qpg_questions_sequence_info)
  );
  const sectionMeta = (
    cloneObject(qpgQuestionsSequenceInfo.questionSectionMetaInfo) || []
  )
    .slice()
    .sort((a, b) => a.sectionNo - b.sectionNo) as QuestionSectionMeta[];

  return (sectionIndex: number, questionIndex?: number, altIdx?: number) => {
    const section = sectionMeta[sectionIndex] as QuestionSectionMeta;
    if (!section) return;
    if (typeof questionIndex !== 'number') {
      return;
    }
    const sectionQuestionsInfo = [...(section.sectionQuestionsInfo || [])];
    sectionQuestionsInfo.sort((a, b) => a.sequence - b.sequence);
    const quesMeta = sectionQuestionsInfo[
      questionIndex
    ] as SectionQuestionMetaData;
    if (!quesMeta) return;
    const outerSequence = quesMeta.sequence;
    const sortedAlternates = [...(quesMeta.alternateQuestions || [])].sort(
      (a, b) => a.sequence - b.sequence
    );
    const allQuestions = [
      new SectionQuestionMetaData({
        questionId: quesMeta.questionId,
        sequence: quesMeta.sequence,
        showMedia: quesMeta.showMedia,
        isAiGenerated: quesMeta.isAiGenerated,
        alternateQuestions: [],
      }),
      ...sortedAlternates.map(
        (val) =>
          new SectionQuestionMetaData({
            questionId: val.questionId,
            sequence: val.sequence,
            showMedia: val.showMedia,
            isAiGenerated: val.isAiGenerated,
            alternateQuestions: [],
          })
      ),
    ];
    let removed: SectionQuestionMetaData[] = [];
    if (typeof altIdx === 'number') {
      removed = allQuestions.splice(altIdx, 1);
    } else {
      // If altIdx is not provided, delete all
      removed = allQuestions.splice(0, allQuestions.length);
    }
    const updatedSectionQuestionsInfo = [...sectionQuestionsInfo];
    if (allQuestions.length > 0) {
      const newSectionQuestionMetaData = new SectionQuestionMetaData({
        ...allQuestions[0],
        alternateQuestions: allQuestions.slice(1).map((val, index) => ({
          ...val,
          sequence: index + 1,
        })),
        sequence: outerSequence,
      });
      updatedSectionQuestionsInfo[questionIndex] = newSectionQuestionMetaData;
    } else {
      updatedSectionQuestionsInfo.splice(questionIndex, 1);
    }
    section.sectionQuestionsInfo = updatedSectionQuestionsInfo;
    sectionMeta[sectionIndex] = section;
    qpgQuestionsSequenceInfo.questionSectionMetaInfo = sectionMeta;
    const removedQuestionIds = removed.map((val) => val.questionId);
    const filteredQuestionIds = qpgQuestionsSequenceInfo.questionIds.filter(
      (id) => !removedQuestionIds.includes(id)
    );
    qpgQuestionsSequenceInfo.questionIds = filteredQuestionIds;
    const filteredQuestionModuleMap = qpg_question_module_map?.filter(
      (val) => !removedQuestionIds.includes(val.questionId || '')
    );
    if (sectionMeta[sectionIndex].sectionQuestionsInfo.length === 0) {
      const updatedQuestionSectionMetaInfo = [
        ...qpgQuestionsSequenceInfo.questionSectionMetaInfo,
      ];
      updatedQuestionSectionMetaInfo.splice(sectionIndex, 1);
      qpgQuestionsSequenceInfo.questionSectionMetaInfo =
        updatedQuestionSectionMetaInfo;
    }
    dispatch(setQPGQuestionsSequenceInfo(qpgQuestionsSequenceInfo));
    const filteredQpgAssessmentQuestions = qpgAssessmentQuestions.filter(
      (ques) => !removedQuestionIds.includes(ques.questionId || '')
    );
    dispatch(setQPGAssessmentContent(filteredQpgAssessmentQuestions));
    dispatch(setQPGQuestionModuleMap(filteredQuestionModuleMap || []));
  };
};

export const useAddOrReplaceQPGQuestion = () => {
  const {
    qpg_assessment_content,
    qpg_questions_sequence_info,
    qpg_question_module_map,
    qpg_question_type_marks,
  } = deserify(useAppSelector((state) => state.assessment));
  const dispatch = useAppDispatch();
  const qpgAssessmentQuestions =
    qpg_assessment_content?.map((ques) => new Question(ques)) || [];
  const qpgQuestionsSequenceInfo = new TaskInfo(qpg_questions_sequence_info);
  const sectionMeta = qpgQuestionsSequenceInfo.questionSectionMetaInfo.sort(
    (a, b) => a.sectionNo - b.sectionNo
  ) as QuestionSectionMeta[];
  return (
    question: Question,
    sectionIndex?: number,
    questionIndex?: number,
    altIdx?: number,
    moduleMapArray?: QuestionModuleMap[]
  ) => {
    // If sectionIndex is not provided, create a new section and add the question there
    if (typeof sectionIndex !== 'number') {
      // Create new SectionQuestionMetaData
      const newQuesMeta = new SectionQuestionMetaData({
        questionId: question.questionId || '',
        sequence: 0,
        showMedia: true,
        isAiGenerated: question.isAiGenerated,
      });
      // Create new QuestionSectionMeta
      const newSection = new QuestionSectionMeta({
        sectionQuestionsInfo: [newQuesMeta],
      });
      // Add new section to sectionMeta
      sectionMeta.push(newSection);
      qpgQuestionsSequenceInfo.questionSectionMetaInfo = sectionMeta;
      qpgQuestionsSequenceInfo.questionIds.push(question.questionId || '');
      qpgAssessmentQuestions.push(question);
      dispatch(setQPGQuestionsSequenceInfo(qpgQuestionsSequenceInfo));
      dispatch(setQPGAssessmentContent(qpgAssessmentQuestions));
      return;
    }
    const section = sectionMeta[sectionIndex] as QuestionSectionMeta;
    if (!section) return;
    // 1. Replace at specific alternate
    if (typeof questionIndex === 'number' && typeof altIdx === 'number') {
      section.sectionQuestionsInfo = section.sectionQuestionsInfo.sort(
        (a, b) => a.sequence - b.sequence
      );
      const quesMeta = section.sectionQuestionsInfo[
        questionIndex
      ] as SectionQuestionMetaData;
      if (!quesMeta) return;
      const allQuestions = [
        quesMeta,
        ...quesMeta.alternateQuestions.sort((a, b) => a.sequence - b.sequence),
      ].map((val) => {
        val.alternateQuestions = [];
        return val;
      });
      const [removed] = allQuestions.splice(altIdx, 1);
      const oldQuestionId = removed.questionId;
      removed.questionId = question.questionId || '';
      allQuestions.splice(altIdx, 0, removed);
      const newSectionQuestionMetaData = new SectionQuestionMetaData({
        ...allQuestions[0],
        alternateQuestions: allQuestions.slice(1).map((val, index) => ({
          ...val,
          sequence: index + 1,
        })),
        sequence: quesMeta.sequence,
      });
      section.sectionQuestionsInfo[questionIndex] = newSectionQuestionMetaData;
      sectionMeta[sectionIndex] = section;
      qpgQuestionsSequenceInfo.questionSectionMetaInfo = sectionMeta;
      // Update questionIds
      const filteredQuestionIds = qpgQuestionsSequenceInfo.questionIds.filter(
        (id) => oldQuestionId !== id
      );
      filteredQuestionIds.push(question.questionId || '');
      qpgQuestionsSequenceInfo.questionIds = filteredQuestionIds;
      const filteredQuestionModuleMap = qpg_question_module_map?.filter(
        (val) => val.questionId !== oldQuestionId
      );
      const currentQuestionModuleMap = moduleMapArray?.find(
        (val) => val.questionId === question.questionId
      );
      if (currentQuestionModuleMap) {
        filteredQuestionModuleMap?.push(currentQuestionModuleMap);
      } else {
        filteredQuestionModuleMap?.push(
          new QuestionModuleMap({
            questionId: question.questionId || '',
            questionType:
              question.questionType ||
              Question_QuestionEnum.QUESTION_TYPE_UNDEFINED,
          })
        );
      }
      dispatch(setQPGQuestionModuleMap(filteredQuestionModuleMap || []));
      // Update assessment questions
      const filteredQpgAssessmentQuestions = qpgAssessmentQuestions.filter(
        (ques) => oldQuestionId !== ques.questionId
      );
      filteredQpgAssessmentQuestions.push(question);
      dispatch(setQPGQuestionsSequenceInfo(qpgQuestionsSequenceInfo));
      dispatch(setQPGAssessmentContent(filteredQpgAssessmentQuestions));
      return;
    }
    // 2. Add as alternate
    if (typeof questionIndex === 'number' && typeof altIdx !== 'number') {
      section.sectionQuestionsInfo = section.sectionQuestionsInfo.sort(
        (a, b) => a.sequence - b.sequence
      );
      const quesMeta = section.sectionQuestionsInfo[
        questionIndex
      ] as SectionQuestionMetaData;
      if (!quesMeta) return;
      if (!quesMeta.alternateQuestions) quesMeta.alternateQuestions = [];
      const maxSequence = Math.max(
        ...quesMeta.alternateQuestions.map((val) => val.sequence)
      );
      quesMeta.alternateQuestions.push(
        new SectionQuestionMetaData({
          questionId: question.questionId || '',
          sequence: maxSequence + 1,
          showMedia: true,
          isAiGenerated: question.isAiGenerated,
        })
      );
      section.sectionQuestionsInfo[questionIndex] = quesMeta;
      sectionMeta[sectionIndex] = section;
      qpgQuestionsSequenceInfo.questionSectionMetaInfo = sectionMeta;
      qpgQuestionsSequenceInfo.questionIds.push(question.questionId || '');
      qpgAssessmentQuestions.push(question);
      dispatch(setQPGQuestionsSequenceInfo(qpgQuestionsSequenceInfo));
      dispatch(setQPGAssessmentContent(qpgAssessmentQuestions));
      return;
    }
    // 3. Add as new question in section
    if (typeof questionIndex !== 'number') {
      section.sectionQuestionsInfo = section.sectionQuestionsInfo.sort(
        (a, b) => a.sequence - b.sequence
      );
      const maxSequence = Math.max(
        ...section.sectionQuestionsInfo.map((val) => val.sequence)
      );
      const newQuesMeta = new SectionQuestionMetaData({
        questionId: question.questionId || '',
        sequence: maxSequence + 1,
        showMedia: true,
        isAiGenerated: question.isAiGenerated,
      });
      section.sectionQuestionsInfo.push(newQuesMeta);
      sectionMeta[sectionIndex] = section;
      qpgQuestionsSequenceInfo.questionSectionMetaInfo = sectionMeta;
      qpgQuestionsSequenceInfo.questionIds.push(question.questionId || '');
      qpgAssessmentQuestions.push(question);
      dispatch(setQPGQuestionsSequenceInfo(qpgQuestionsSequenceInfo));
      dispatch(setQPGAssessmentContent(qpgAssessmentQuestions));
      return;
    }
  };
};

export const useShowMediaClick = () => {
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const { isOffline } = useDownloadContext();

  const { qpg_questions_sequence_info } = deserify(
    useAppSelector((state) => state.assessment)
  );
  const dispatch = useAppDispatch();
  const qpgQuestionsSequenceInfo = new TaskInfo(qpg_questions_sequence_info);
  const sectionMeta = qpgQuestionsSequenceInfo.questionSectionMetaInfo.sort(
    (a, b) => a.sectionNo - b.sectionNo
  ) as QuestionSectionMeta[];

  return async (
    sectionIndex: number,
    questionIndex: number,
    altIdx: number
  ) => {
    // If sectionIndex is not provided, create a new section and add the question there
    const section = sectionMeta[sectionIndex] as QuestionSectionMeta;
    if (!section) return;
    // 1. Replace at specific alternate
    section.sectionQuestionsInfo = section.sectionQuestionsInfo.sort(
      (a, b) => a.sequence - b.sequence
    );
    const quesMeta = section.sectionQuestionsInfo[
      questionIndex
    ] as SectionQuestionMetaData;
    if (!quesMeta) return;
    const allQuestions = [
      quesMeta,
      ...quesMeta.alternateQuestions.sort((a, b) => a.sequence - b.sequence),
    ].map((val) => {
      val.alternateQuestions = [];
      return val;
    });

    await interactionEvent({
      context: 'Question_paper_generator',
      name: allQuestions[altIdx].showMedia
        ? 'INCLUDE_IMAGE_NO'
        : 'INCLUDE_IMAGE_YES',
      pathSegments: pathSegments,
      isOffline: isOffline,
    });

    allQuestions[altIdx].showMedia = !allQuestions[altIdx].showMedia;
    const newSectionQuestionMetaData = new SectionQuestionMetaData({
      ...allQuestions[0],
      alternateQuestions: allQuestions.slice(1).map((val, index) => ({
        ...val,
        sequence: index + 1,
      })),
      sequence: quesMeta.sequence,
    });
    section.sectionQuestionsInfo[questionIndex] = newSectionQuestionMetaData;
    sectionMeta[sectionIndex] = section;
    qpgQuestionsSequenceInfo.questionSectionMetaInfo = sectionMeta;
    // Update questionIds
    dispatch(setQPGQuestionsSequenceInfo(qpgQuestionsSequenceInfo));
    return;
  };
};

export function numberToAlphabet(num: number) {
  let result = '';
  while (num >= 0) {
    result = String.fromCharCode((num % 26) + 64) + result;
    num = Math.floor(num / 26) - 1;
  }
  return result;
}

export function compressRanges(nums: number[]) {
  if (!nums.length) return [];
  nums.sort((a, b) => a - b); // ✅ sort numbers first
  const result = [];
  let start = nums[0];
  let end = nums[0];
  for (let i = 1; i <= nums.length; i++) {
    if (nums[i] === end + 1) {
      end = nums[i]; // continue range
    } else {
      // push current range
      if (start === end) {
        result.push([start]); // single number
      } else {
        result.push([start, end]); // range
      }
      start = nums[i];
      end = nums[i];
    }
  }
  return result;
}

export function getAllIndexes(arr: any[], conditionFn: (item: any) => boolean) {
  return arr.reduce((indexes, item, idx) => {
    if (conditionFn(item)) indexes.push(idx);
    return indexes;
  }, []);
}

export const questionCountTypeWise = (list: Question[]) =>
  list.reduce(
    (acc, ques) => {
      const questionType = ques?.questionType;
      const type =
        (questionType
          ? getQuestionTypeTitleAndEnum(questionType)?.title
          : undefined) || 'Unknown';
      if (acc[type]) {
        acc[type].count++;
        acc[type].marks +=
          ques?.question?.model.value?.commonQuestionContent?.marks[0] || 0;
      } else {
        acc[type] = {
          count: 1,
          marks:
            ques?.question?.model.value?.commonQuestionContent?.marks[0] || 0,
          type: questionType,
        };
      }
      return acc;
    },
    {} as Record<
      string,
      {
        count: number;
        marks: number;
        type: Question_QuestionEnum | undefined;
      }
    >
  );
export const getBloomLevelPercentage = (
  bloomLevel: BloomLevelEnum,
  questionsList: Question[]
) => {
  const totalQuestions =
    questionsList?.filter((ques) => !!ques?.questionMeta?.bloomLevel)?.length ||
    0;
  const questionsWithBloomLevel =
    questionsList?.filter(
      (ques) => ques?.questionMeta?.bloomLevel === bloomLevel
    )?.length || 0;
  return totalQuestions ? (questionsWithBloomLevel / totalQuestions) * 100 : 0;
};

export const getBloomLevelData = (questionsList: Question[]) => {
  return [
    {
      title: 'Retention',
      color: '#005DAA',
      Questions: getBloomLevelPercentage(
        BloomLevelEnum.BLOOMLEVEL_RETENTION,
        questionsList
      ),
    },
    {
      title: 'Analysis',
      color: '#F1A33B',
      Questions: getBloomLevelPercentage(
        BloomLevelEnum.BLOOMLEVEL_ANALYSIS,
        questionsList
      ),
    },
    {
      title: 'Application',
      color: '#009383',
      Questions: getBloomLevelPercentage(
        BloomLevelEnum.BLOOMLEVEL_APPLICATION,
        questionsList
      ),
    },
    {
      title: 'Comprehension',
      color: '#5DC152',
      Questions: getBloomLevelPercentage(
        BloomLevelEnum.BLOOMLEVEL_COMPREHENSION,
        questionsList
      ),
    },
    {
      title: 'Creation',
      color: '#00bfc8',
      Questions: getBloomLevelPercentage(
        BloomLevelEnum.BLOOMLEVEL_CREATION,
        questionsList
      ),
    },
    {
      title: 'Evaluation',
      color: '#e80000',
      Questions: getBloomLevelPercentage(
        BloomLevelEnum.BLOOMLEVEL_EVALUATION,
        questionsList
      ),
    },
  ];
};

export const getQuestionNumberFromIndex = (
  sectionIndex: number,
  questionIndex: number,
  questionSectionMetaInfo: QuestionSectionMeta[]
) => {
  let questionNumber = 0;
  for (let i = 0; i < sectionIndex; i++) {
    questionNumber +=
      questionSectionMetaInfo[i]?.sectionQuestionsInfo?.length || 0;
  }
  questionNumber += questionIndex;
  return questionNumber + 1;
};

// Utility function for hash using crypto-js
export function getStringHash(str: string): string {
  return md5(str);
}

export const questionHasImage = (question?: Question) => {
  const ques = new Question(question);
  const commonQuestionContent =
    ques.question?.model.value?.commonQuestionContent;
  return (
    commonQuestionContent?.elements?.some(
      (element) => element.model.value instanceof ImageType
    ) ||
    commonQuestionContent?.solution.some(
      (element) => element.model.value instanceof ImageType
    )
  );
};

export const useAddOrReplaceQPGQuestionMultiSubject = () => {
  const { qpg_content } = deserify(useAppSelector((state) => state.assessment));
  const assessmentContent = qpg_content?.assessmentContent || [];
  const dispatch = useAppDispatch();
  return (
    question: Question,
    sectionIndex: number,
    subSectionIndex: number,
    questionIndex?: number,
    altIdx?: number,
    moduleMapArray?: QuestionModuleMap[]
  ) => {
    console.log({
      question,
      sectionIndex,
      subSectionIndex,
      questionIndex,
      altIdx,
      moduleMapArray,
    });
    const section = qpg_content?.multiSubjectSections[sectionIndex];
    if (!section) return;
    const subSection = section.multiSubjectSubSections[subSectionIndex];
    if (!subSection) return;

    console.log({
      section,
      subSection,
    });

    // 1. Add as new question (append to section when index is past the end)
    if (
      typeof questionIndex !== 'number' ||
      questionIndex === subSection.subSectionMetaData.length
    ) {
      const meta = subSection.subSectionMetaData;
      const maxSequence =
        meta.length > 0 ? Math.max(...meta.map((val) => val.sequence ?? 0)) : 0;
      const questionType = question.questionType;
      const subSectionQuestionTypeMarks = subSection.questionTypeAndMarks;
      const questionTypeMarks = subSectionQuestionTypeMarks.find(
        (val) => val.questionType === questionType
      );
      if (
        questionTypeMarks &&
        question?.question?.model?.value?.commonQuestionContent
      ) {
        question.question.model.value.commonQuestionContent.marks[0] =
          questionTypeMarks.marks;
      }
      const newQuesMeta = new SectionQuestionMetaData({
        questionId: question.questionId || '',
        sequence: maxSequence + 1,
        showMedia: true,
        isAiGenerated: question.isAiGenerated,
        maxMarks:
          question?.question?.model?.value?.commonQuestionContent?.marks[0] ??
          0,
      });
      subSection.subSectionMetaData.push(newQuesMeta);
      section.multiSubjectSubSections[subSectionIndex] = subSection;
      const updatedContent = cloneObject(qpg_content);
      updatedContent.multiSubjectSections[sectionIndex] = section;
      const ifNewQuestionExistInOtherSection =
        updatedContent.multiSubjectSections?.some((sec, secIdx) =>
          sec.multiSubjectSubSections?.some((subSec, subSecIdx) =>
            subSec.subSectionMetaData?.some(
              (ques, qIdx) =>
                (secIdx !== sectionIndex || subSecIdx !== subSectionIndex) &&
                (ques.questionId === question.questionId ||
                  (ques.alternateQuestions ?? []).some(
                    (alt) => alt.questionId === question.questionId
                  ))
            )
          )
        );
      console.log({ ifNewQuestionExistInOtherSection });
      if (!ifNewQuestionExistInOtherSection) {
        updatedContent.assessmentContent = [...assessmentContent, question];
        if (updatedContent.questionsSequenceInfo) {
          updatedContent.questionsSequenceInfo.questionModuleMap = [
            ...(updatedContent.questionsSequenceInfo.questionModuleMap ?? []),
            ...(moduleMapArray?.filter(
              (val) => val.questionId === question.questionId
            ) ?? []),
          ];
        }
      }
      dispatch(setQPGContent(updatedContent || undefined));
      return;
    }

    const quesMeta = subSection.subSectionMetaData[questionIndex];
    if (!quesMeta) return;

    // 2. Replace at specific alternate (main = 0, first alternate = 1, ...)
    if (typeof altIdx === 'number') {
      const sortedAlternates = [...(quesMeta.alternateQuestions ?? [])].sort(
        (a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0)
      );
      const allQuestions = [
        new SectionQuestionMetaData({
          questionId: quesMeta.questionId,
          sequence: quesMeta.sequence,
          showMedia: quesMeta.showMedia,
          isAiGenerated: quesMeta.isAiGenerated,
          maxMarks: quesMeta.maxMarks ?? 0,
          questionType: quesMeta.questionType,
          alternateQuestions: [],
        }),
        ...sortedAlternates.map(
          (val) =>
            new SectionQuestionMetaData({
              questionId: val.questionId,
              sequence: val.sequence,
              showMedia: val.showMedia,
              isAiGenerated: val.isAiGenerated,
              maxMarks: val.maxMarks ?? 0,
              questionType: val.questionType,
              alternateQuestions: [],
            })
        ),
      ];
      const [removed] = allQuestions.splice(altIdx, 1);
      const deletedQuestionId = removed.questionId;
      const questionType = question.questionType;
      const subSectionQuestionTypeMarks = subSection.questionTypeAndMarks;
      const questionTypeMarks = subSectionQuestionTypeMarks.find(
        (val) => val.questionType === questionType
      );
      if (
        questionTypeMarks &&
        question?.question?.model?.value?.commonQuestionContent
      ) {
        question.question.model.value.commonQuestionContent.marks[0] =
          questionTypeMarks.marks;
      }
      removed.questionId = question.questionId || '';
      removed.maxMarks =
        question?.question?.model?.value?.commonQuestionContent?.marks[0] ?? 0;
      if (question.isAiGenerated !== undefined)
        removed.isAiGenerated = question.isAiGenerated;
      allQuestions.splice(altIdx, 0, removed);
      const newSectionQuestionMetaData = new SectionQuestionMetaData({
        questionId: allQuestions[0].questionId,
        sequence: quesMeta.sequence,
        showMedia: allQuestions[0].showMedia,
        isAiGenerated: allQuestions[0].isAiGenerated,
        maxMarks: allQuestions[0].maxMarks ?? 0,
        questionType: allQuestions[0].questionType,
        alternateQuestions: allQuestions.slice(1).map(
          (val, index) =>
            new SectionQuestionMetaData({
              questionId: val.questionId,
              sequence: index + 1,
              showMedia: val.showMedia,
              isAiGenerated: val.isAiGenerated,
              maxMarks: val.maxMarks ?? 0,
              questionType: val.questionType,
              alternateQuestions: [],
            })
        ),
      });
      subSection.subSectionMetaData[questionIndex] = newSectionQuestionMetaData;
      section.multiSubjectSubSections[subSectionIndex] = subSection;
      const updatedContent = cloneObject(qpg_content);
      updatedContent.multiSubjectSections[sectionIndex] = section;
      const checkIfDeletedQuesExistInOtherSections =
        updatedContent.multiSubjectSections?.some((sec, secIdx) =>
          sec.multiSubjectSubSections?.some((subSec, subSecIdx) =>
            subSec.subSectionMetaData?.some(
              (ques, qIdx) =>
                (secIdx !== sectionIndex ||
                  subSecIdx !== subSectionIndex ||
                  qIdx !== questionIndex) &&
                (ques.questionId === deletedQuestionId ||
                  (ques.alternateQuestions ?? []).some(
                    (alt) => alt.questionId === deletedQuestionId
                  ))
            )
          )
        );
      if (checkIfDeletedQuesExistInOtherSections) {
        updatedContent.assessmentContent =
          updatedContent.assessmentContent.filter(
            (ques) => ques.questionId !== deletedQuestionId
          );
        if (updatedContent.questionsSequenceInfo) {
          updatedContent.questionsSequenceInfo.questionModuleMap =
            updatedContent.questionsSequenceInfo.questionModuleMap?.filter(
              (val) => val.questionId !== deletedQuestionId
            ) ?? [];
        }
      }
      const ifNewQuestionExistInOtherSection =
        updatedContent.multiSubjectSections?.some((sec, secIdx) =>
          sec.multiSubjectSubSections?.some((subSec, subSecIdx) =>
            subSec.subSectionMetaData?.some(
              (ques, qIdx) =>
                (secIdx !== sectionIndex ||
                  subSecIdx !== subSectionIndex ||
                  qIdx !== questionIndex) &&
                (ques.questionId === question.questionId ||
                  (ques.alternateQuestions ?? []).some(
                    (alt) => alt.questionId === question.questionId
                  ))
            )
          )
        );
      if (!ifNewQuestionExistInOtherSection) {
        updatedContent.assessmentContent = [...assessmentContent, question];
        if (updatedContent.questionsSequenceInfo) {
          updatedContent.questionsSequenceInfo.questionModuleMap = [
            ...(updatedContent.questionsSequenceInfo.questionModuleMap ?? []),
            ...(moduleMapArray?.filter(
              (val) => val.questionId === question.questionId
            ) ?? []),
          ];
        }
      }
      dispatch(setQPGContent(updatedContent || undefined));
      return;
    }

    // 3. Add as alternate
    const sequences =
      quesMeta.alternateQuestions?.map((val) => val.sequence ?? 0) ?? [];
    const maxSequence = sequences.length > 0 ? Math.max(...sequences) : 0;
    const questionType = question.questionType;
    const subSectionQuestionTypeMarks = subSection.questionTypeAndMarks;
    const questionTypeMarks = subSectionQuestionTypeMarks.find(
      (val) => val.questionType === questionType
    );
    if (
      questionTypeMarks &&
      question?.question?.model?.value?.commonQuestionContent
    ) {
      question.question.model.value.commonQuestionContent.marks[0] =
        questionTypeMarks.marks;
    }
    quesMeta.alternateQuestions = quesMeta.alternateQuestions ?? [];
    quesMeta.alternateQuestions.push(
      new SectionQuestionMetaData({
        questionId: question.questionId || '',
        sequence: maxSequence + 1,
        showMedia: true,
        isAiGenerated: question.isAiGenerated,
        maxMarks:
          question?.question?.model?.value?.commonQuestionContent?.marks[0] ??
          0,
      })
    );
    subSection.subSectionMetaData[questionIndex] = quesMeta;
    section.multiSubjectSubSections[subSectionIndex] = subSection;
    const updatedContent = cloneObject(qpg_content);
    updatedContent.multiSubjectSections[sectionIndex] = section;
    const ifNewQuestionExistInOtherSection =
      updatedContent.multiSubjectSections?.some((sec, secIdx) =>
        sec.multiSubjectSubSections?.some((subSec, subSecIdx) =>
          subSec.subSectionMetaData?.some(
            (ques, qIdx) =>
              (secIdx !== sectionIndex ||
                subSecIdx !== subSectionIndex ||
                qIdx !== questionIndex) &&
              (ques.questionId === question.questionId ||
                (ques.alternateQuestions ?? []).some(
                  (alt) => alt.questionId === question.questionId
                ))
          )
        )
      );
    if (!ifNewQuestionExistInOtherSection) {
      updatedContent.assessmentContent = [...assessmentContent, question];
      if (updatedContent.questionsSequenceInfo) {
        updatedContent.questionsSequenceInfo.questionModuleMap = [
          ...(updatedContent.questionsSequenceInfo.questionModuleMap ?? []),
          ...(moduleMapArray?.filter(
            (val) => val.questionId === question.questionId
          ) ?? []),
        ];
      }
    }
    dispatch(setQPGContent(updatedContent || undefined));
  };
};

export const useShowMediaClickMultiSubject = () => {
  const { qpg_content } = deserify(useAppSelector((state) => state.assessment));
  const dispatch = useAppDispatch();

  return (
    sectionIndex: number,
    subSectionIndex: number,
    questionIndex: number,
    altIdx: number
  ) => {
    const section = qpg_content?.multiSubjectSections[sectionIndex];
    if (!section) return;
    const subSection = section.multiSubjectSubSections[subSectionIndex];
    if (!subSection) return;
    const quesMeta = subSection.subSectionMetaData[questionIndex];
    if (!quesMeta) return;

    const sortedAlternates = [...(quesMeta.alternateQuestions ?? [])].sort(
      (a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0)
    );
    const allQuestions = [
      new SectionQuestionMetaData({
        questionId: quesMeta.questionId,
        sequence: quesMeta.sequence,
        showMedia: quesMeta.showMedia,
        isAiGenerated: quesMeta.isAiGenerated,
        maxMarks: quesMeta.maxMarks ?? 0,
        questionType: quesMeta.questionType,
        alternateQuestions: [],
      }),
      ...sortedAlternates.map(
        (val) =>
          new SectionQuestionMetaData({
            questionId: val.questionId,
            sequence: val.sequence,
            showMedia: val.showMedia,
            isAiGenerated: val.isAiGenerated,
            maxMarks: val.maxMarks ?? 0,
            questionType: val.questionType,
            alternateQuestions: [],
          })
      ),
    ];
    const target = allQuestions[altIdx];
    if (target) target.showMedia = !target.showMedia;

    const newSectionQuestionMetaData = new SectionQuestionMetaData({
      questionId: allQuestions[0].questionId,
      sequence: quesMeta.sequence,
      showMedia: allQuestions[0].showMedia,
      isAiGenerated: allQuestions[0].isAiGenerated,
      maxMarks: allQuestions[0].maxMarks ?? 0,
      questionType: allQuestions[0].questionType,
      alternateQuestions: allQuestions.slice(1).map(
        (val, index) =>
          new SectionQuestionMetaData({
            questionId: val.questionId,
            sequence: index + 1,
            showMedia: val.showMedia,
            isAiGenerated: val.isAiGenerated,
            maxMarks: val.maxMarks ?? 0,
            questionType: val.questionType,
            alternateQuestions: [],
          })
      ),
    });
    subSection.subSectionMetaData[questionIndex] = newSectionQuestionMetaData;
    section.multiSubjectSubSections[subSectionIndex] = subSection;
    const updatedContent = cloneObject(qpg_content);
    updatedContent.multiSubjectSections[sectionIndex] = section;
    dispatch(setQPGContent(updatedContent || undefined));
  };
};

export const useDeleteQPGQuestionMultiSubject = () => {
  const { qpg_content } = deserify(useAppSelector((state) => state.assessment));
  const dispatch = useAppDispatch();

  return (
    sectionIndex: number,
    subSectionIndex: number,
    questionIndex: number,
    altIdx?: number
  ) => {
    const section = qpg_content?.multiSubjectSections[sectionIndex];
    if (!section) return;
    const subSection = section.multiSubjectSubSections[subSectionIndex];
    if (!subSection) return;
    const quesMeta = subSection.subSectionMetaData[questionIndex];
    if (!quesMeta) return;

    const sortedAlternates = [...(quesMeta.alternateQuestions ?? [])].sort(
      (a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0)
    );
    const allQuestions = [
      new SectionQuestionMetaData({
        questionId: quesMeta.questionId,
        sequence: quesMeta.sequence,
        showMedia: quesMeta.showMedia,
        isAiGenerated: quesMeta.isAiGenerated,
        maxMarks: quesMeta.maxMarks ?? 0,
        questionType: quesMeta.questionType,
        alternateQuestions: [],
      }),
      ...sortedAlternates.map(
        (val) =>
          new SectionQuestionMetaData({
            questionId: val.questionId,
            sequence: val.sequence,
            showMedia: val.showMedia,
            isAiGenerated: val.isAiGenerated,
            maxMarks: val.maxMarks ?? 0,
            questionType: val.questionType,
            alternateQuestions: [],
          })
      ),
    ];

    let removed: SectionQuestionMetaData[] = [];
    if (typeof altIdx === 'number') {
      removed = allQuestions.splice(altIdx, 1);
    } else {
      removed = allQuestions.splice(0, allQuestions.length);
    }

    const removedQuestionIds = removed.map((r) => r.questionId);

    if (allQuestions.length > 0) {
      const newSectionQuestionMetaData = new SectionQuestionMetaData({
        questionId: allQuestions[0].questionId,
        sequence: quesMeta.sequence,
        showMedia: allQuestions[0].showMedia,
        isAiGenerated: allQuestions[0].isAiGenerated,
        maxMarks: allQuestions[0].maxMarks ?? 0,
        questionType: allQuestions[0].questionType,
        alternateQuestions: allQuestions.slice(1).map(
          (val, index) =>
            new SectionQuestionMetaData({
              questionId: val.questionId,
              sequence: index + 1,
              showMedia: val.showMedia,
              isAiGenerated: val.isAiGenerated,
              maxMarks: val.maxMarks ?? 0,
              questionType: val.questionType,
              alternateQuestions: [],
            })
        ),
      });
      subSection.subSectionMetaData[questionIndex] = newSectionQuestionMetaData;
    } else {
      subSection.subSectionMetaData.splice(questionIndex, 1);
    }
    section.multiSubjectSubSections[subSectionIndex] = subSection;
    const updatedContent = cloneObject(qpg_content);
    updatedContent.multiSubjectSections[sectionIndex] = section;

    for (const deletedId of removedQuestionIds) {
      const existsElsewhere = updatedContent.multiSubjectSections?.some(
        (sec, secIdx) =>
          sec.multiSubjectSubSections?.some((subSec, subSecIdx) =>
            subSec.subSectionMetaData?.some(
              (ques, qIdx) =>
                (secIdx !== sectionIndex ||
                  subSecIdx !== subSectionIndex ||
                  qIdx !== questionIndex) &&
                (ques.questionId === deletedId ||
                  (ques.alternateQuestions ?? []).some(
                    (alt) => alt.questionId === deletedId
                  ))
            )
          )
      );
      if (!existsElsewhere) {
        updatedContent.assessmentContent =
          updatedContent.assessmentContent.filter(
            (q) => q.questionId !== deletedId
          );
        if (updatedContent.questionsSequenceInfo) {
          updatedContent.questionsSequenceInfo.questionModuleMap =
            updatedContent.questionsSequenceInfo.questionModuleMap?.filter(
              (val) => val.questionId !== deletedId
            ) ?? [];
        }
      }
    }

    if (updatedContent.questionModuleMap?.length) {
      const idsStillUsed = new Set<string>();
      updatedContent.multiSubjectSections?.forEach((sec) =>
        sec.multiSubjectSubSections?.forEach((subSec) =>
          subSec.subSectionMetaData?.forEach((ques) => {
            idsStillUsed.add(ques.questionId || '');
            (ques.alternateQuestions ?? []).forEach((alt) =>
              idsStillUsed.add(alt.questionId || '')
            );
          })
        )
      );
      if (updatedContent.questionsSequenceInfo) {
        updatedContent.questionsSequenceInfo.questionModuleMap =
          updatedContent.questionsSequenceInfo.questionModuleMap?.filter((m) =>
            idsStillUsed.has(m.questionId || '')
          ) ?? [];
      }
    }

    dispatch(setQPGContent(updatedContent || undefined));
  };
};
